import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';

import Message from 'components/Message';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import { isRequiredGroup, previewValuesFromRules } from './CustomerPreview.helpers';
import messages from './CustomerPreview.messages';
import './CustomerPreview.scss';

const { block, element } = bem('sku-customer-preview');

const renderSelector = (option, selector) => {
  if (selector === 'quantity') {
    return (
      <div className={element('stepper')} aria-hidden="true">
        <span className={element('stepper-button', { disabled: option.isDisabled })}>+</span>
      </div>
    );
  }

  if (selector === 'radio') {
    return (
      <span
        className={element('radio', { selected: option.isSelected, disabled: option.isDisabled })}
        aria-hidden="true"
      />
    );
  }

  return (
    <span
      className={element('checkbox', { selected: option.isSelected, disabled: option.isDisabled })}
      aria-hidden="true">
      {option.isSelected && <Isvg className={element('checkbox-icon')} src={ICONS.checkmark} />}
    </span>
  );
};

const renderMetaLine = (groupDescription, instruction) => {
  if (!groupDescription && !instruction) {
    return null;
  }

  return (
    <p className={element('group-meta')}>
      {groupDescription && <span>{groupDescription}</span>}
      {groupDescription && instruction && <span className={element('meta-separator')}> · </span>}
      {instruction && <Message message={instruction.message} values={instruction.values} />}
    </p>
  );
};

function CustomerPreview({ groupDescription, groupName, itemName, options, rules }) {
  const preview = previewValuesFromRules(rules, options);
  const required = isRequiredGroup(preview.selectionsMin);
  const displayItemName = itemName || null;

  return (
    <aside className={block()}>
      <div className={element('header')}>
        <h3 className={element('title')}>
          <Message message={messages.title} />
        </h3>
        <p className={element('subheader')}>
          <Message message={messages.subheader} />
        </p>
      </div>

      <div className={element('phone-card')}>
        <div className={element('item-image')} aria-hidden="true" />

        <div className={element('item-details')}>
          <div className={element('item-name')}>
            {displayItemName || (
              <span className={element('placeholder')}>
                <Message message={messages.itemNamePlaceholder} />
              </span>
            )}
          </div>

          <div className={element('group')}>
            <div className={element('group-header')}>
              <div className={element('group-heading')}>
                {groupName && <h4 className={element('group-name')}>{groupName}</h4>}
                {renderMetaLine(groupDescription, preview.instruction)}
              </div>
              <span className={element('badge', { required, optional: !required })}>
                <Message message={required ? messages.requiredBadge : messages.optionalBadge} />
              </span>
            </div>

            {preview.previewOptions.length > 0 && (
              <ul className={element('options')}>
                {preview.previewOptions.map(option => (
                  <li key={option.id} className={element('option', { disabled: option.isDisabled })}>
                    <div className={element('option-copy')}>
                      <span className={element('option-name')}>
                        {option.name ? (
                          option.name
                        ) : (
                          <span className={element('placeholder')}>
                            <Message message={messages.optionNamePlaceholder} />
                          </span>
                        )}
                      </span>
                      {option.description && (
                        <span className={element('option-description')}>{option.description}</span>
                      )}
                    </div>
                    {renderSelector(option, preview.selector)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}

CustomerPreview.defaultProps = {
  groupDescription: '',
  groupName: '',
  itemName: '',
  options: [],
  rules: {},
};

CustomerPreview.propTypes = {
  groupDescription: PropTypes.string,
  groupName: PropTypes.string,
  itemName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      enabled: PropTypes.bool,
      id: PropTypes.string.isRequired,
      isDefault: PropTypes.bool,
      name: PropTypes.string,
      ordinal: PropTypes.number,
      soldOut: PropTypes.bool,
    })
  ),
  rules: PropTypes.shape({
    allowsMultiple: PropTypes.bool,
    allowsQuantities: PropTypes.bool,
    defaultOptionId: PropTypes.string,
    hasDefault: PropTypes.bool,
    isRequired: PropTypes.bool,
    maxSelection: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minSelection: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default CustomerPreview;
